import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useState, useEffect } from 'react';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
}

interface MediaItem {
  id: string;
  url: string;
  altText: string;
  caption?: string;
  order: number;
}

interface ServiceDetailProps {
  service: Service;
  isOpen: boolean;
  onClose: () => void;
}

const ServiceDetail = ({ service, isOpen, onClose }: ServiceDetailProps) => {
  const { t, getTranslation } = useTranslation();
  const [serviceMedia, setServiceMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Get service details from translations
  const serviceData = getTranslation(`services.items.${service.id}`) || {};
  const fullDescription = serviceData.fullDescription || '';

  useEffect(() => {
    // In static mode we do not load media from backend
    setServiceMedia([]);
    setLoading(false);
  }, [isOpen, service.id]);

  const getServiceImages = (serviceId: string): string[] => {
    // Return service.image if available, otherwise empty array (will use default from card)
    return service.image ? [service.image] : [];
  };

  // Use service media if available, otherwise fallback to default images
  const imagesToShow = serviceMedia.length > 0 
    ? serviceMedia.map(item => ({ url: item.url, alt: item.altText, caption: item.caption }))
    : getServiceImages(service.id).map((url, index) => ({ 
        url, 
        alt: `${service.title} ${index + 1}`, 
        caption: undefined 
      }));

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
          <Dialog.Portal>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-50"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-4 bg-white rounded-xl max-w-4xl mx-auto my-auto overflow-y-auto z-50 max-h-[90vh]"
              >
                <div className="relative">
                  <Dialog.Close className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors z-10">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Dialog.Close>

                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={imagesToShow[0]?.url || service.image || ''} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                    <div className="absolute bottom-0 left-0 p-6">
                      <Dialog.Title className="text-3xl font-bold text-white">
                        {service.title}
                      </Dialog.Title>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Service Description */}
                    <div className="prose max-w-none mb-8">
                      {fullDescription.split('\n').map((line: string, index: number) => (
                        line.trim().startsWith('1.') || line.trim().startsWith('2.') || line.trim().startsWith('3.') || line.trim().startsWith('4.') ?
                          <h3 key={index} className="font-bold mt-6 mb-2 text-primary-800">{line.trim()}</h3> :
                          line.trim() && !line.trim().startsWith('1.') && !line.trim().startsWith('2.') && !line.trim().startsWith('3.') && !line.trim().startsWith('4.') ?
                            <p key={index} className="mb-4 text-gray-700 leading-relaxed">{line.trim()}</p> :
                            null
                      ))}
                    </div>

                    {/* Image Gallery */}
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700"></div>
                      </div>
                    ) : imagesToShow.length > 0 && (
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Pildigalerii</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {imagesToShow.map((image, index) => (
                            <div key={index} className="relative group overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow">
                              <img 
                                src={image.url} 
                                alt={image.alt}
                                className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                              {image.caption && (
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                                  <p className="text-white text-sm font-medium">{image.caption}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* CTA Section */}
                    <div className="bg-primary-50 rounded-lg p-6 text-center">
                      <h4 className="text-lg font-semibold text-primary-800 mb-2">
                        Huvitatud sellest teenusest?
                      </h4>
                      <p className="text-primary-700 mb-4">
                        Võtke meiega ühendust tasuta konsultatsiooni saamiseks
                      </p>
                      <a 
                        href="/kontakt"
                        onClick={onClose} 
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 transition-colors"
                      >
                        {t('services.contactUs')}
                        <svg className="ml-2 -mr-1 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </AnimatePresence>
  );
};

export default ServiceDetail;